import { useEffect, useRef, useState } from "react";

/*
  Das Feld, in das die Randbemerkung geschrieben wird.

  Es geht dort auf, wo eben noch der Finger war — genau wie „Wohin damit?“ und
  aus demselben Grund: Wer eine Stelle im Text ausgewählt hat, schaut dorthin.
  Ein Kasten am unteren Bildschirmrand zwingt den Blick weg und den Daumen quer
  über das Tablet.

  Wer eine bestehende Notiz bearbeitet, bekommt zusätzlich „Entfernen“.
  Entfernt wird dabei nur der Text, nicht die Markierung: Aus der Notiz
  wird wieder eine bloße Markierung, die Stelle bleibt im Dokument gelb.
*/

const RAND = 12;

/*
  Wie breit das Feld ist — und warum das hier gerechnet und nicht
  hingeschrieben wird.

  ---------------------------------------------------------------
  Die Grundschrift dieses Projekts ist 18 Pixel, nicht 16
  ---------------------------------------------------------------

  Im Stilblatt steht `.zziel { width: 20rem }` (`bausteine.css`). `rem`
  rechnet gegen die Schriftgröße von `html`, und die steht in
  `grundlagen.css` auf 18px — wegen der Zielgruppe, von der ein Teil über
  siebzig ist. 20rem sind hier also 360 Pixel.

  Hier stand vorher fest 320: der Wert, der herauskäme, wenn die
  Grundschrift die üblichen 16 Pixel hätte. Vierzig Pixel Unterschied fallen
  auf einem breiten Bildschirm nicht auf, weil dort ohnehin nichts geklemmt
  wird. Auf einem iPhone (390 Pixel) ragte das Feld dadurch 28 Pixel über
  den rechten Rand hinaus — nachgemessen am 2. August 2026.

  Die Zahl 20 muss weiterhin zur CSS-Regel passen. Die UMRECHNUNG kann jetzt
  nicht mehr danebengehen.
*/
const BREITE_REM = 20;

function breite() {
  const grundschrift = parseFloat(getComputedStyle(document.documentElement).fontSize);
  return BREITE_REM * (grundschrift || 18);
}

/*
  Wie hoch das Feld ungefähr wird. Nachgemessen am 2. August 2026: 389 Pixel
  ohne „Notiz entfernen", mit dem Knopf entsprechend mehr. 420 ist bewusst
  etwas großzügig — lieber einmal zu früh nach oben ausweichen als das Feld
  unten abschneiden. Eine Schätzung reicht: Passt es weder oben noch unten,
  greift ohnehin der dritte Fall mit `max-height`.
*/
const HOEHE = 420;

export default function Notizfeld({
  zitat,
  stelle,
  wert = "",
  privat = false,
  aufSichern,
  aufEntfernen,
  aufAbbrechen,
}) {
  const [text, setzeText] = useState(wert);
  const feldRef = useRef(null);

  useEffect(() => {
    if (feldRef.current) feldRef.current.focus();
  }, []);

  useEffect(() => {
    const beiTaste = (e) => {
      if (e.key === "Escape") aufAbbrechen();
    };
    window.addEventListener("keydown", beiTaste);
    return () => window.removeEventListener("keydown", beiTaste);
  }, [aufAbbrechen]);

  const lage = platziere(stelle);
  const leer = !text.trim();

  return (
    <>
      <button className="zziel__hinterfeld" aria-label="Abbrechen" onClick={aufAbbrechen} />

      <div className="zziel" style={lage} role="dialog" aria-label="Notiz schreiben">
        <p className="zziel__frage">Was fällt dir dazu auf?</p>
        <blockquote className="zziel__zitat">„{kuerze(zitat)}“</blockquote>

        {/*
          Wer an eine private Markierung etwas schreibt, muss wissen, dass die
          Notiz damit privat bleibt — sonst wartet er auf eine Reaktion der
          Fraktion, die niemand sehen kann.
        */}
        {privat && (
          <p className="zziel__lage">
            <strong>Diese Stelle ist nur für dich sichtbar.</strong> Auch deine Notiz sieht
            dann niemand sonst.
          </p>
        )}

        <textarea
          ref={feldRef}
          className="notizfeld"
          placeholder="Zum Beispiel: Warum das aus deiner Sicht falsch ist, oder was fehlt."
          value={text}
          onChange={(e) => setzeText(e.target.value)}
        />

        <div className="zziel__wahl">
          <button className="knopf" disabled={leer} onClick={() => aufSichern(text.trim())}>
            Notiz sichern
          </button>
        </div>

        <div className="zziel__fuss">
          {aufEntfernen && (
            <button className="knopf knopf--schlicht" onClick={aufEntfernen}>
              Notiz entfernen
            </button>
          )}
          <button className="knopf knopf--schlicht" onClick={aufAbbrechen}>
            Abbrechen
          </button>
        </div>
      </div>
    </>
  );
}

/* Wie bei „Wohin damit?“: unterhalb der Stelle, wenn dort Platz ist, sonst darüber. */
/*
  Wohin mit dem Feld?

  ---------------------------------------------------------------
  Der Wächter in der ersten Zeile ist das Wichtigste hier
  ---------------------------------------------------------------

  Erwartet wird ein Rechteck in Bildschirmkoordinaten: `links`, `oben`,
  `unten`. Kommt etwas anderes an, wurde bis hierher trotzdem gerechnet —
  mit `undefined`. Das ergibt `left: "undefinedpx"`, und der Browser wirft
  ungültige Werte kommentarlos weg. Das Feld war dann nicht etwa mittig,
  sondern GAR NICHT platziert und stand irgendwo weit außerhalb des
  Bildschirms. Kein Fehler in der Konsole, nichts.

  Deshalb wird die Form jetzt geprüft. Stimmt sie nicht, geht das Feld
  mittig auf: sichtbar und bedienbar, auch wenn die Stelle verloren ist.
  Ein Kommentar an ungünstiger Stelle ist ein Schönheitsfehler — ein
  Kommentar, den man nicht schreiben kann, ist ein kaputter Knopf.
*/
function platziere(stelle) {
  const mittig = { left: "50%", top: "20%", transform: "translateX(-50%)" };
  if (!stelle) return mittig;
  if (typeof stelle.links !== "number" || typeof stelle.oben !== "number") return mittig;
  if (typeof stelle.unten !== "number") return mittig;

  const fensterBreite = window.innerWidth;
  const fensterHoehe = window.innerHeight;
  const kastenBreite = breite();

  let links = stelle.links;
  if (links + kastenBreite > fensterBreite - RAND) links = fensterBreite - kastenBreite - RAND;
  if (links < RAND) links = RAND;

  /*
    Erst unter die Stelle, sonst darüber — und wenn beides zu eng ist, oben
    festmachen und scrollen lassen. Auf einem Handy im Querformat ist der
    dritte Fall keine Ausnahme, sondern der Normalfall.
  */
  if (fensterHoehe - stelle.unten >= HOEHE + RAND) {
    return { left: `${links}px`, top: `${stelle.unten + 8}px` };
  }
  if (stelle.oben >= HOEHE + RAND) {
    return { left: `${links}px`, bottom: `${fensterHoehe - stelle.oben + 8}px` };
  }
  return {
    left: `${links}px`,
    top: `${RAND}px`,
    maxHeight: `${fensterHoehe - 2 * RAND}px`,
    overflowY: "auto",
  };
}

function kuerze(text, laenge = 120) {
  return text.length <= laenge ? text : `${text.slice(0, laenge).trimEnd()} …`;
}
